import { useState } from 'react';
import { toast } from 'sonner';
import { Check, CircleHelp } from 'lucide-react';
import { api } from '../api';
import type { JsonDTO } from '../../../src/domain/json';
import type { QuestionInbox, QuestionView } from '../../../src/application/questions';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { DeskSection, EmptyNote, formatMoment, useDeskData } from '../components/desk';
import { useAgentName } from '../agent-name';
import './questions.css';

type Inbox = JsonDTO<QuestionInbox> & { canAnswer: boolean; canAnswerForAll: boolean };
type Question = JsonDTO<QuestionView>;
const topicText = (topic: string) => topic.replace(/_/g, ' ');
const percent = (share: number) => `${Math.round(share * 100)}%`;

/**
 * Questions for you (#218): what Carol is waiting to be told, one card each with why she asks and the answer she would give.
 * "Use this answer" approves her recommendation in one click; an answer is kept and she does not ask it again.
 */
export function Questions() {
  const persona = useAgentName();
  const { data, error } = useDeskData<Inbox>('/app-api/questions');
  const stats = data?.stats;
  return <main id="main-content" className="queue-page desk-page questions-page">
    <header className="page-heading"><div><h1>Questions for you<span className="heading-dot" aria-hidden="true">.</span></h1>
      <p>{data ? data.open.length ? `${persona} is waiting on ${data.open.length} ${data.open.length === 1 ? 'answer' : 'answers'}. She sends nothing to those customers until you answer.`
        : `${persona} has nothing to ask right now.` : 'Reading the questions…'}</p></div></header>
    {error && <p role="alert" className="muted">{error}</p>}
    {data && <>
      {data.open.length === 0
        ? <EmptyNote icon={<Check aria-hidden />} title="Nothing to answer">When {persona} lacks a fact she needs, such as which PO to use, she asks here once and keeps your answer.</EmptyNote>
        : <div className="question-list">{data.open.map(q => <QuestionCard key={q.id} q={q} canAnswer={data.canAnswer} canAnswerForAll={data.canAnswerForAll} />)}</div>}
      {stats && <DeskSection title="Learning" aside={<span className="muted">last 14 days</span>}>
        <p className="question-stats">
          <span><strong>{stats.asked}</strong> asked</span><span><strong>{stats.answered}</strong> answered</span>
          <span><strong>{stats.avoided}</strong> not asked again</span><span><strong>{stats.merged}</strong> repeats merged</span>
          <span><strong>{percent(stats.avoidedShare)}</strong> of questions avoided</span>
        </p>
        <p className="muted">The share avoided grows as {persona} learns your answers.</p>
      </DeskSection>}
      {data.recent.length > 0 && <DeskSection title="Recently settled">
        <ul className="question-recent">{data.recent.map(q => <li key={q.id}>
          <a href={`#customers/${encodeURIComponent(q.customerId)}`}>{q.customerName}</a>
          <span>{q.question}</span>
          <em>{q.status === 'answered' ? `${q.answer}${q.answerScope === 'org' ? ' (for every customer)' : ''}` : 'Not needed'}</em>
          {q.closedAt && <time dateTime={q.closedAt}>{formatMoment(q.closedAt)}</time>}
        </li>)}</ul>
      </DeskSection>}
    </>}
  </main>;
}

function QuestionCard({ q, canAnswer, canAnswerForAll }: { q: Question; canAnswer: boolean; canAnswerForAll: boolean }) {
  const persona = useAgentName();
  const [text, setText] = useState(''); const [everyone, setEveryone] = useState(false); const [busy, setBusy] = useState(false);
  async function run(path: string, body: object, done: string) {
    setBusy(true);
    try { await api(`/app-api/questions/${encodeURIComponent(q.id)}/${path}`, body); toast.success(done); }
    catch (e) { toast.error(e instanceof Error ? e.message : 'That did not complete'); }
    finally { setBusy(false); }
  }
  const scope = everyone && q.topic ? { scope: 'org' } : {};
  return <article className="question-card" aria-label={q.question}>
    <header><a href={`#customers/${encodeURIComponent(q.customerId)}`}>{q.customerName}</a>
      {q.topic && <span className="question-topic">{topicText(q.topic)}</span>}
      {q.askedTimes > 1 && <span className="muted">asked {q.askedTimes} times</span>}</header>
    <h3><CircleHelp size={16} aria-hidden />{q.question}</h3>
    <p className="muted">{q.context}</p>
    {canAnswer ? <>
      {q.recommended && <div className="question-recommended">
        <span>{persona} suggests</span><strong>{q.recommended}</strong>
        <Button size="sm" disabled={busy} onClick={() => void run('answer', { useRecommended: true, ...scope }, 'Answer saved')}><Check aria-hidden />Use this answer</Button>
      </div>}
      <label className="question-field"><span className="sr-only">Your answer</span>
        <Textarea value={text} maxLength={600} placeholder="Or write your own answer" onChange={e => setText(e.target.value)} /></label>
      <footer>
        {canAnswerForAll && q.topic && <label className="question-everyone"><input type="checkbox" checked={everyone} onChange={e => setEveryone(e.target.checked)} />Applies to every customer</label>}
        <Button variant="outline" size="sm" disabled={busy} onClick={() => void run('dismiss', {}, 'Closed as not needed')}>Not needed</Button>
        <Button size="sm" disabled={busy || !text.trim()} onClick={() => void run('answer', { answer: text.trim(), ...scope }, 'Answer saved')}>Save answer</Button>
      </footer>
    </> : <p className="muted">An approver can answer this.</p>}
  </article>;
}
